/**
 * @file userService.js
 * @description APIs for User related requests.
 **/

import { contentHttp } from './httpService';
// import { readEachContainer } from './containerService';

/**
 * Retrieve the tenant ID from local storage.
 * @returns {string|null} The tenant ID or null if not found.
 */
const getTenantId = () => +window.localStorage.getItem('tenantId');

/**
 *
 * @returns {} - A promise that resolve to the user onboarding details
 */

export const createPortalPage = async payload => {
    const tenantId = localStorage.getItem('tenantId') || 0;

    const draftPayload = { ...payload };
    if ('draftOfPortalId' in draftPayload && draftPayload.draftOfPortalId) {
        draftPayload.publishDate = null;
    }
    if (!draftPayload.displayName) {
        draftPayload.displayName = draftPayload.name;
    }
    try {
        const response = await contentHttp.post(
            `/v2/content/tenant/${tenantId}/portal_pages`,
            draftPayload,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error fetching current user data:', error);
        throw error;
    }
};

/**
 * @returns {} - A promise that resolve to the user onboarding details
 */

export const queryPortalPages = async (
    isChild,
    tenantId = null,
    isTemplate = false,
    searchTerm = null,
    drafts = false,
) => {
    if (!tenantId) tenantId = getTenantId();
    //TODO: clean this up
    let params = [],
        url = `/v2/content/tenant/${tenantId}/portal_pages`;

    if (isChild !== null) {
        params.push('isChild=' + isChild);
    }

    if (isTemplate) {
        params.push('isTemplate=true');
    }

    if (searchTerm && searchTerm.trim() !== '') {
        params.push(`searchTerm=${encodeURIComponent(searchTerm.trim())}`);
    }

    if (drafts) {
        params.push('drafts=' + drafts);
    }

    if (params.length > 0) {
        url += `?${params.join('&')}`;
    }

    try {
        const response = await contentHttp.get(url, {
            skipTenantHeader: true,
        });

        //TODO: bake this into the APIs and remove this
        let portalPages = response.data.viewModels;
        portalPages.forEach(page => {
            if (!page.populationGroups) {
                page.populationGroups = [];
            }
        });
        return response?.data || null;
    } catch (error) {
        console.error('Error fetching current user data:', error);
        throw error;
    }
};

/**
 * Retrieve scoped portal pages for the calling user with no additional params.
 * @returns {Promise<any>} The raw response data from the API.
 */
export const queryScopedPortalPages = async () => {
    try {
        const response = await contentHttp.get('/v2/content/portal_pages');
        return response?.data || null;
    } catch (error) {
        console.error('Error fetching scoped portal pages:', error);
        throw error;
    }
};

/**
 * Retrieve scoped portal pages for the calling user with no additional params.
 * @returns {Promise<any>} The raw response data from the API.
 */
export const readScopedPortalPage = async ID => {
    try {
        const response = await contentHttp.get(
            `/v2/content/portal_pages/single/${ID}`,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error fetching scoped portal page:', error);
        throw error;
    }
};

/**
 * @ID - The ID of the page to get
 * @loadContainers - Indicates if container payload should be loaded
 *
 * @returns {} - A promise that resolve to the user onboarding details
 */

export const readPortalPage = async (ID, loadContainers, tenantId = null) => {
    if (tenantId === null) {
        tenantId = localStorage.getItem('tenantId') || 0;
    }
    try {
        const url = `/v2/content/tenant/${tenantId}/portal_pages/single/${ID}`;

        const response = await contentHttp.get(url, {
            skipTenantHeader: true,
            params: {
                containers: loadContainers,
            },
        });

        //TODO: bake this into the APIs and remove this
        let portalPages = response.data.viewModels;
        portalPages.forEach(page => {
            if (!page.populationGroups) {
                page.populationGroups = [];
            }
        });

        return response?.data || null;
    } catch (error) {
        console.error('Error fetching current user data:', error);
        throw error;
    }
};

/**
 *
 * @returns {} - A promise that resolve to the user onboarding details
 */

export const updatePortalPage = async updatedPortalPage => {
    const tenantId = localStorage.getItem('tenantId') || 0;

    try {
        const response = await contentHttp.put(
            `/v2/content/tenant/${tenantId}/portal_pages`,
            updatedPortalPage,
        );

        return response?.data || null;
    } catch (error) {
        console.error(`Error updating portal page`, error);
        throw error;
    }
};

/**
 *
 * @returns {} - A promise that resolve to the user onboarding details
 */

export const deletePortalPage = async ID => {
    //NOT SUPPORTED? RETURNING 405 ERROR

    const tenantId = localStorage.getItem('tenantId') || 0;

    try {
        const response = await contentHttp.delete(
            `/v2/content/tenant/${tenantId}/portal_pages/${ID}`,
        );

        return response.data;
    } catch (error) {
        console.error('Error deleting portal pages:', error);
        throw error;
    }
};

/**
 * Retrieves the current user's data.
 *
 * @returns {Promise<object>} A promise that resolves to the current user's data.
 */
export const getCurrentUser = async () => {
    try {
        const response = await contentHttp.get('/v1/user/current', {
            skipTenantHeader: true,
        });
        return response?.data?.users[0] || null;
    } catch (error) {
        console.error('Error fetching current user data:', error);
        throw error;
    }
};

/**
 * Updates the order of the Portal Tabs on the Home page.
 *
 * @param {Array} portalTabs - An array of objects containing PortalId and Order.
 * @returns {Promise<object>} A promise that resolves to the response data.
 */
export const updatePortalTabsOrder = async portalTabs => {
    const tenantId = localStorage.getItem('tenantId');

    const requestBody = {
        PortalOrders: portalTabs,
    };

    try {
        const response = await contentHttp.put(
            `/v2/content/tenant/${tenantId}/portal_pages/order`,
            requestBody,
        );

        return response?.data || null;
    } catch (error) {
        console.error('Error updating portal tabs order:', error);
        throw error;
    }
};

export const createDraft = async portalId => {
    const tenantId = localStorage.getItem('tenantId') || 0;
    try {
        const response = await contentHttp.post(
            `/v2/content/tenant/${tenantId}/portal_pages/single/${portalId}/draft`,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error creating draft:', error);
        throw error;
    }
};

export const promoteDraft = async portalId => {
    const tenantId = localStorage.getItem('tenantId') || 0;
    try {
        const response = await contentHttp.post(
            `/v2/content/tenant/${tenantId}/portal_pages/single/${portalId}/draft/promote`,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error promoting draft:', error);
        throw error;
    }
};

export const discardDraft = async portalId => {
    const tenantId = localStorage.getItem('tenantId') || 0;
    try {
        const response = await contentHttp.delete(
            `/v2/content/tenant/${tenantId}/portal_pages/single/${portalId}/draft`,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error discarding draft:', error);
        throw error;
    }
};
